<template>
  <div class="view-inspector">
    <el-divider class="view-inspector__divider" />
    <div class="view-inspector__content">
      <div class="view-inspector__top">
        <div class="view-inspector__top-left">
          <div class="view-inspector__title">{{ field.title }}</div>
          <div class="view-inspector__target">
            Target ID: {{ field.target }}
          </div>
        </div>
        <experiment-status-tag :status="field.status" />
        <img
          :src="field.application?.icon_full_url"
          class="view-inspector__image"
        />
      </div>

      <div class="view-inspector__description" v-html="field.description" />
      <div class="view-inspector__date-wrapper">
        <div class="view-inspector__date">{{ statisticDate }}</div>
        <el-button
          type="danger"
          size="small"
          text
          :disabled="isViewer"
          @click="cleanData"
        >
          Clean results<el-icon class="el-icon--right"><IcClear /></el-icon>
        </el-button>
      </div>
    </div>
    <el-tabs
      v-if="!isQaEngineer"
      v-model="activeTab"
      @tab-change="handleTabChange"
    >
      <el-tab-pane label="Production" name="production" />
      <el-tab-pane label="Test" name="test" />
    </el-tabs>
    <ProgressStatistic class="view-inspector__progress" :items="statistic" />
    <StatisticSignificance
      :significance="significance"
      :statistic="statistic"
    />
    <div
      class="view-inspector__variant"
      v-for="(variant, index) in statistic"
      :key="variant.id"
    >
      <el-divider />
      <StatisticVariant
        :percentage="variant.percentage"
        :success="variant.success_count"
        :total="variant.users_count"
        :successPercentage="variant.success_percentage"
        :title="variant.title"
        :index="index"
      />
    </div>
    <div
      class="view-inspector__history"
      v-if="field.status_logs && field.status_logs.length"
    >
      <el-divider />
      <div class="view-inspector__history-title">History</div>
      <div
        class="view-inspector__history-item"
        v-for="log in field.status_logs"
        :key="log.id"
      >
        <div class="view-inspector__history-tags">
          <el-tag
            class="view-inspector__history-tag"
            :type="getTypeByStatus(log.from_status)"
            >{{ log.from_status || "Ready" }}</el-tag
          >
          <el-icon><Right /></el-icon>
          <el-tag
            class="view-inspector__history-tag"
            :type="getTypeByStatus(log.to_status)"
            >{{ log.to_status || "Ready" }}</el-tag
          >
        </div>
        <div class="view-inspector__history-date">
          {{ formatDate(log.updated_at || log.created_at) }}
        </div>
      </div>
    </div>
    <div class="view-inspector__bottom">
      <div class="view-inspector__buttons">
        <StatusButton
          :status="field.status"
          :loading="loadingChangeStatus"
          :disabled="isViewer"
          @click="changeStatus"
        />
        <div class="view-inspector__button-right">
          <el-button
            class="view-inspector__button-delete"
            :icon="Delete"
            :disabled="isViewer"
            @click="onDelete"
          ></el-button>
          <el-tooltip
            :disabled="canEdit"
            content="Stop the test to edit"
            placement="top"
          >
            <el-button
              class="view-inspector__button-edit"
              :icon="IcEdit"
              :disabled="!canEdit"
              @click="onEdit"
            ></el-button>
          </el-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from "vue";
import { useStore } from "vuex";
import { Delete, Right } from "@element-plus/icons-vue";
import { StatisticSignificance } from "@/shared/ui/components/StatisticSignificance";
import { StatisticVariant } from "@/shared/ui/components/StatisticVariant";
import { ProgressStatistic } from "@/shared/ui/components/ProgressStatistic";
import { StatusButton } from "@/shared/ui/components/StatusButton";
import IcEdit from "@/shared/ui/icons/IcEdit.vue";
import IcClear from "@/shared/ui/icons/IcClear.vue";
import formatDate from "@/shared/lib/formatDate";
import { ElMessageBox } from "element-plus";
import ExperimentStatusTag from "@/shared/ui/components/ExperimentStatusTag";

const store = useStore();

const props = defineProps({
  field: {
    type: Object,
    default: () => ({}),
  },
  statistic: {
    type: Array,
    default: () => [],
  },
  significance: {
    type: Object,
    default: () => ({}),
  },
  id: {
    type: Number,
  },
  environment: {
    type: String,
  },
});

const emit = defineEmits(["submit", "cancel", "delete", "on-tab-change"]);

const activeTab = ref("production");
const loadingChangeStatus = ref(false);

const statisticDate = computed(() => {
  if (props.field.start_date || props.field.created_at) {
    if (props.field.end_date) {
      return `${formatDate(
        props.field.start_date || props.field.created_at
      )} - ${formatDate(props.field.end_date)}`;
    }
    return `${formatDate(
      props.field.start_date || props.field.created_at
    )} - till now`;
  } else {
    return `Test hasn't been started`;
  }
});

const isViewer = computed(() => {
  return store.state.auth.roles.includes("viewer");
});

const isQaEngineer = computed(() => {
  return store.state.auth.roles.includes("qa_engineer");
});

const canEdit = computed(() => {
  return !isViewer.value && props.field.status !== "active";
});

function onEdit() {
  emit("edit");
}

function onDelete() {
  emit("delete", props.field.id);
}

const getTypeByStatus = (status) => {
  switch (status) {
    case "ready":
      return "primary";
    case "testing":
      return "success";
    case "done":
      return "info";
    default:
      return "primary";
  }
};

const cleanData = async () => {
  ElMessageBox.confirm("", "Are you sure?", {
    confirmButtonText: "Clean data",
    cancelButtonText: "Cancel",
    cancelButtonClass: "cancel-button",
    confirmButtonClass: "confirm-button",
    showClose: false,
  }).then(async () => {
    await store.dispatch("experiments/resetStatistics", {
      experiment_id: props.field.id,
      environment: props.environment,
    });
  });
};

const changeStatus = async () => {
  loadingChangeStatus.value = true;
  await store.dispatch("experiments/toggleExperiment", props.field.id);
  loadingChangeStatus.value = false;
};

const handleTabChange = () => {
  emit("on-tab-change", activeTab.value);
};

watch(
  () => props.id,
  () => {
    activeTab.value = isQaEngineer.value ? "test" : "production";
  }
);
</script>

<style lang="scss">
.view-inspector {
  overflow: auto;
  height: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-bottom: 186px;
  &::-webkit-scrollbar {
    display: none;
  }
  .el-form-item {
    margin-bottom: 8px !important;
  }
  .el-form-item__label {
    margin-bottom: 0 !important;
  }

  &__divider {
    margin: 16px 0 !important;
  }

  &__top {
    gap: 8px;
    display: grid;
    grid-template-columns: 1fr auto 40px;
  }

  &__image {
    width: var(--Size-common-component-size-large, 40px);
    height: var(--Size-common-component-size-large, 40px);
    flex-shrink: 0;
    border-radius: var(--Radius-border-radius-base, 4px);
  }

  &__title {
    font-size: 20px;
    font-weight: 800;
    line-height: 28px;
  }
  &__target {
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 500;
    line-height: 22px;
  }
  &__description {
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }
  &__date-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__date {
    overflow: hidden;
    color: var(--Color-Text-text-color-secondary, #909399);
    text-overflow: ellipsis;
    font-family: Inter;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }
  &__date-wrapper {
    margin-bottom: 16px;
  }
  &__progress {
    margin-bottom: 16px;
  }
  &__history-title {
    color: var(--Color-Text-text-color-secondary, #909399);
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
  }
  &__history-item {
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__history-tags {
    display: flex;
    align-items: center;
  }
  &__history-date {
    color: var(--Color-Text-text-color-regular, #606266);
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
  }
  &__bottom {
    border-top: 1px solid #e4e7ed;
    background-color: rgb(242, 243, 245);
    padding: 24px;
    width: 100%;
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: 1;
  }
  &__buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__button-delete .el-icon {
    color: rgba(245, 108, 108, 1);
  }
}
</style>
